<template>
  <div>
    <v-btn
      icon
      color="primary"
      size="small"
      @click="openDialog"
      :disabled="disabled"
    >
      <v-icon>mdi-pencil</v-icon>
    </v-btn>
    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit License</span>
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateLicense" ref="form">
            <v-text-field v-model="editedLicense.machine_id" label="Machine ID" required readonly></v-text-field>
            <v-text-field v-model="editedLicense.client_name" label="Client Name" required></v-text-field>
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              transition="scale-transition"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  :value="formattedDate"
                  label="Valid Date"
                  readonly
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="computedDate"
                @update:modelValue="menu = false"
              ></v-date-picker>
            </v-menu>
            <v-text-field
              v-model.number="editedLicense.friendly_prompt_period"
              label="Friendly Prompt Period (days)"
              type="number"
              required
            ></v-text-field>
            <v-text-field
              v-model.number="editedLicense.restricted_warning_period"
              label="Restricted Warning Period (days)"
              type="number"
              required
            ></v-text-field>
            <v-text-field
              v-model="editedLicense.client_contact"
              label="Client Contact"
            ></v-text-field>
            <v-text-field
              v-model="editedLicense.address"
              label="Address"
            ></v-text-field>
            <div class="permissions-tree">
              <div v-for="genre in genreCategories" :key="genre.genre.inner_id" class="genre-item">
                <v-checkbox
                  v-model="selectedGenres"
                  :label="genre.genre.name"
                  :value="genre.genre.inner_id"
                  @change="updateCategorySelection(genre)"
                ></v-checkbox>
                <div v-for="category in genre.categories" :key="category.inner_id" class="category-item">
                  <v-checkbox
                    v-model="selectedCategories"
                    :label="category.name"
                    :value="category.inner_id"
                    :disabled="!selectedGenres.includes(genre.genre.inner_id)"
                  ></v-checkbox>
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="updateLicense">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import yaml from 'js-yaml'

const props = defineProps({
  license: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['license-updated'])

const dialog = ref(false)
const menu = ref(false)
const editedLicense = ref({})
const genreCategories = ref([])
const selectedGenres = ref([])
const selectedCategories = ref([])

const computedDate = computed({
  get: () => {
    if (!editedLicense.value.valid_date) return null

    return parseDate(editedLicense.value.valid_date)
  },
  set: (newValue) => {
    if (!newValue) return
    const date = new Date(newValue)
    editedLicense.value.valid_date = formatDateForAPI(date)
  }
})

const formattedDate = computed(() => {
  if (!editedLicense.value.valid_date) return ''
  const date = parseDate(editedLicense.value.valid_date)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
})

onMounted(async () => {
  await fetchGenreCategories()
})

function parseDate(dateString) {
  const [datePart, timePart] = dateString.split('-')
  const year = parseInt(datePart.substring(0, 4))
  const month = parseInt(datePart.substring(4, 6)) - 1 // JS months are 0-indexed
  const day = parseInt(datePart.substring(6, 8))
  const hours = parseInt(timePart.substring(0, 2))
  const minutes = parseInt(timePart.substring(2, 4))
  const seconds = parseInt(timePart.substring(4, 6))
  return new Date(year, month, day, hours, minutes, seconds)
}

function formatDateForAPI(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

function openDialog() {
  editedLicense.value = { ...props.license }
  selectedGenres.value = props.license.genres || []
  selectedCategories.value = props.license.categories || []
  dialog.value = true
}

function closeDialog() {
  dialog.value = false
  // Reset the form
  editedLicense.value = { ...props.license }
  selectedGenres.value = props.license.genres || []
  selectedCategories.value = props.license.categories || []
}

async function fetchGenreCategories() {
  try {
    const response = await axios.get('/api/genre-category-yaml', {
      responseType: 'text'
    })
    const yamlData = response.data
    const jsonData = yaml.load(yamlData)
    genreCategories.value = jsonData
  } catch (error) {
    console.error('Error fetching genre categories:', error)
  }
}

function updateCategorySelection(genre) {
  const genreId = genre.genre.inner_id
  if (selectedGenres.value.includes(genreId)) {
    genre.categories.forEach(category => {
      const categoryId = category.inner_id
      if (!selectedCategories.value.includes(categoryId)) {
        selectedCategories.value.push(categoryId)
      }
    })
  } else {
    selectedCategories.value = selectedCategories.value.filter(id =>
        !genre.categories.some(category => category.inner_id === id)
    )
  }
}

async function updateLicense() {
  try {
    await axios.put(`/api/licenses/${editedLicense.value.machine_id}`, {
      ...editedLicense.value,
      genres: selectedGenres.value,
      categories: selectedCategories.value
    })
    emit('license-updated')
    closeDialog()
  } catch (error) {
    console.error('Error updating license:', error)
  }
}
</script>

<style scoped>
.category-item {
  margin-left: 20px;
}
</style>